import { create } from "zustand";
import { persist } from "zustand/middleware";
import { Language, defaultLanguage } from "../internationalization/languages";

export interface UIState {
  // Theme state
  theme: "light" | "dark";

  // Language state
  currentLanguage: Language;

  // Navigation state
  isNavCollapsed: boolean;

  // Header state
  headerBreadcrumb: React.ReactNode | null;
  headerRightSide: React.ReactNode | null;

  // Actions
  setTheme: (theme: "light" | "dark") => void;
  setLanguage: (language: Language) => void;
  setNavCollapsed: (collapsed: boolean) => void;
  setHeaderBreadcrumb: (content: React.ReactNode) => void;
  setHeaderRightSide: (content: React.ReactNode) => void;
  clearHeader: () => void;

  // Computed values
  isDarkMode: boolean;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Initial state
      theme: "light",
      currentLanguage: defaultLanguage,
      isNavCollapsed: false,
      headerBreadcrumb: null,
      headerRightSide: null,

      // Actions
      setTheme: (theme: "light" | "dark") => {
        set({ theme });
      },

      setLanguage: (language: Language) => {
        set({ currentLanguage: language });
      },

      setNavCollapsed: (collapsed: boolean) => {
        set({ isNavCollapsed: collapsed });
      },

      setHeaderBreadcrumb: (content: React.ReactNode) => {
        set({ headerBreadcrumb: content });
      },

      setHeaderRightSide: (content: React.ReactNode) => {
        set({ headerRightSide: content });
      },

      clearHeader: () => {
        set({
          headerBreadcrumb: null,
          headerRightSide: null,
        });
      },

      // Computed values
      get isDarkMode() {
        return get().theme === "dark";
      },
    }),
    {
      name: "ui-storage",
      partialize: (state) => ({
        theme: state.theme,
        currentLanguage: state.currentLanguage,
        isNavCollapsed: state.isNavCollapsed,
      }),
    }
  )
);
